import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { ArrowUp, ArrowDown, X } from "lucide-react";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import { Switch } from "@/components/design/Switch";
import { TextInput } from "@/components/design/TextField";
import { ListingApplicationsDocument, SetListingApplicationProcessDocument } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import type { ApplicationProcess } from "./listing-application-process";

export function ListingProcessSettings({ listingId, process, onClose }: {
  listingId: string; process: ApplicationProcess; onClose: () => void;
}) {
  const intl = useIntl();
  const msg = (key: string) => intl.formatMessage({ id: `share.listings.process.${key}` });
  const [draft, setDraft] = useState(process);
  const [save, { loading }] = useMutation(SetListingApplicationProcessDocument, { refetchQueries: [ListingApplicationsDocument] });
  const [error, setError] = useState<string | null>(null);
  const [remove, setRemove] = useState<string | null>(null);
  function move(index: number, delta: number) {
    const stages = [...draft.stages];
    [stages[index], stages[index + delta]] = [stages[index + delta]!, stages[index]!];
    setDraft({ ...draft, stages });
  }
  async function submit() {
    if (loading) return;
    setError(null);
    try {
      await save({ variables: { input: { listingId, process: { ...draft } } } });
      onClose();
    } catch (e) { setError(userErrorMessage(intl, e)); }
  }
  return <Modal id="listing-process-settings" open onClose={onClose} title={msg("settings")} footer={<>
    <Button id="listing-process-settings-cancel" variant="secondary" onClick={onClose} disabled={loading}>{intl.formatMessage({ id: "common.cancel" })}</Button>
    <Button variant="primary" onClick={() => void submit()} disabled={loading || draft.stages.some(s => !s.name.trim())}>{intl.formatMessage({ id: "common.save" })}</Button>
  </>}>
    <ol className="space-y-3">
      {draft.stages.map((stage, index) => <li key={stage.id} className="grid grid-cols-[1rem_minmax(0,1fr)_auto] items-center gap-2">
        <span className="text-smycles-meta text-ink-muted">{index + 1}</span>
        <TextInput aria-label={intl.formatMessage({ id: "share.listings.process.phaseNumber" }, { number: index + 1 })}
          maxLength={80} value={stage.name} disabled={loading}
          onChange={e => setDraft({ ...draft, stages: draft.stages.map(s => s.id === stage.id ? { ...s, name: e.target.value } : s) })} />
        <div className={`flex items-center gap-1 ${index < 2 ? "invisible" : ""}`} aria-hidden={index < 2 || undefined}>
          <Button variant="icon" size="sm" aria-label={msg("up")} disabled={loading || index <= 2} onClick={() => move(index, -1)}><ArrowUp size={14} /></Button>
          <Button variant="icon" size="sm" aria-label={msg("down")} disabled={loading || index < 2 || index === draft.stages.length - 1} onClick={() => move(index, 1)}><ArrowDown size={14} /></Button>
          {remove === stage.id ? <Button variant="destructive" size="compact" disabled={loading} onClick={() => {
            setDraft({ ...draft, stages: draft.stages.filter(s => s.id !== stage.id) }); setRemove(null);
          }}>{msg("removeConfirm")}</Button> : <Button variant="icon" size="sm" aria-label={msg("remove")} disabled={loading || index < 2} onClick={() => setRemove(stage.id)}><X size={14} /></Button>}
        </div>
      </li>)}
    </ol>
    <Button className="mt-4" variant="secondary" size="compact" disabled={loading || draft.stages.length >= 12}
      onClick={() => setDraft({ ...draft, stages: [...draft.stages, { id: crypto.randomUUID(), name: msg("newPhase") }] })}>{msg("add")}</Button>
    <div className="mt-6 flex items-center justify-between gap-4 border-t border-ink/10 pt-5">
      <span className="text-smycles-body">{msg("quizEnabled")}</span>
      <Switch ariaLabel={msg("quizEnabled")} checked={draft.powerQuizEnabled} disabled={loading}
        onChange={powerQuizEnabled => setDraft({ ...draft, powerQuizEnabled })} />
    </div>
    {error ? <p role="alert" className="mt-4 text-smycles-meta text-ink-accent">{error}</p> : null}
  </Modal>;
}
